import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft, Building2, Check, ChevronLeft, ChevronRight, Clock, Globe, MessageSquareText, Phone, Plus, Sparkles, Users, Video,
} from "lucide-react";
import { Button, Input, Label, Skeleton, Textarea } from "@/components/ui";
import { formatDate, formatLocalDateKey, formatMoney, formatTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { bookingSections } from "@/lib/booking-sections";

// ─── Types (shape of /api/public/booking/*) ────────────────────────────────
interface Business { name: string; timezone: string; logo_url: string | null }
interface Category {
  id: string; name: string; color: string; position: number;
  /** The section this reads as on the booking page. Two categories sharing
   *  one group are shown as one section; empty = a section of its own. */
  booking_group?: string | null;
}
interface Service {
  id: string; category_id: string | null; name: string; description: string | null;
  duration_minutes: number; price_cents: number; color: string; provider_ids: string[];
  /** A first-time client may book this (IV drips, first consultations). */
  new_clients?: boolean;
  /** Its mirror: off means this is a first visit's treatment only. */
  returning_clients?: boolean;
  /** Ways it can happen, from its seats: in the lounge, by video, by phone. */
  modes?: Mode[];
  /** Her own line about THIS treatment, read before they pick it. */
  booking_note?: string | null;
  /** Which of her three things this belongs to, and whether it is the
   *  consultation for it. Drives the "what brings you in" step. */
  form_packet?: string | null;
  counts_as_consult?: boolean;
}
/** "a choice of what the new person wants" — one per thing she treats. */
interface BookingPath {
  key: string; label: string; blurb: string; packets: string[];
  /** How a first-timer may be seen on this path. Absent = any the treatment allows. */
  new_client_modes?: Mode[];
  /** What online booking offers here to anybody, new or returning. */
  online_modes?: Mode[];
  /** An enquiry, not a booking: take details and ring back. */
  enquiry?: { slug: string; note: string };
}
/** Texting consent, word for word from the API (lib/sms-consent.ts). */
interface SmsConsent {
  version: string;
  appointment_notice: string;
  offers_consent: string;
  terms_url: string;
  privacy_url: string;
}
interface Menu {
  business: Business; categories: Category[]; services: Service[]; sms?: SmsConsent;
  /** New clients only see new_clients treatments; returning clients see all. */
  new_client_limit?: boolean;
  /** Her own line above the list for a first-time client. */
  first_visit_note?: string;
  /** The same, for somebody who has been in before. */
  returning_note?: string;
  /** "a choice of what the new person wants" — only paths she can actually
   *  book a consultation for. */
  paths?: BookingPath[];
  /** "we need to write a blurb at the point of when they are choosing the
   *  consultation how it works" — hers, from Settings. */
  consult_note?: string;
}
interface Slot { start: string; end: string; provider_id: string | null }

// No "provider" step: "When we book we have to remove the staff names."
// Who performs a treatment is the practice's business, and the booking takes
// whoever is free.
type Step = "path" | "service" | "mode" | "date" | "time" | "details" | "done";

/** How a consult happens. Asked only when the treatment can go more than one way. */
type Mode = "in_person" | "video" | "phone";
const MODE_LABEL: Record<Mode, string> = { in_person: "In person", video: "Video call", phone: "Phone call" };
const modePhrase = (m: Mode | null) => (m === "phone" ? " by phone" : m === "video" ? " by video call" : "");

/**
 * Seat / category / provider colours arrive as palette NAMES ("sky",
 * "emerald"), which aren't CSS colours — used raw they drew nothing, so the
 * category dots and provider initials were invisible. The 400 shade of each.
 */
const PALETTE_HEX: Record<string, string> = {
  violet: "#a78bfa", purple: "#c084fc", fuchsia: "#e879f9", pink: "#f472b6", rose: "#fb7185",
  red: "#f87171", orange: "#fb923c", amber: "#fbbf24", yellow: "#facc15", green: "#4ade80",
  emerald: "#34d399", teal: "#2dd4bf", cyan: "#22d3ee", sky: "#38bdf8", blue: "#60a5fa",
  indigo: "#818cf8", zinc: "#a1a1aa", slate: "#94a3b8", gray: "#9ca3af", stone: "#a8a29e",
};
const paletteHex = (c: string | null | undefined, fallback = "#71717a") =>
  !c ? fallback : c.startsWith("#") ? c : PALETTE_HEX[c] ?? fallback;

/** "First available" sentinel vs. a concrete provider vs. not-yet-chosen. */
/** Nobody is asked to choose a person any more, so this is only "we have a
 *  time" or "we don't yet": the booking takes whoever the engine picked. */
type ProviderPick = "any" | null;

/** Asked first when the practice limits what new clients can book online. */
type Visitor = "new" | "returning";

/** The API accepts at most five treatments in one visit. */
const MAX_SERVICES = 5;

/**
 * Public med-spa self-booking page. Clients walk through:
 *   (new or returning?) → treatment → provider → date → time → details → done
 *
 * "If new ppl they are only able to schedule a iv / consultation? If
 * returning they can schedule any service?" A new client only sees the
 * treatments marked for new clients; a returning client sees everything and
 * is matched to their file by the mobile number or email they give (the
 * book route enforces it, so a mismatch comes back with a way out).
 *
 * One treatment is the common path; a client can stack up to five into a
 * single visit ("+ Add another treatment"), and the whole chain is priced,
 * scheduled and booked as one appointment.
 *
 * Mounted at /booking as a public, no-auth route — everything here talks to
 * /api/public/booking/* with raw fetch (no auth headers, no 401 reload).
 */
export function MedspaBookingPage() {
  /**
   * "Actually it should push re schedule or cancel" — the change-or-cancel
   * page sends them here with ?reschedule=<token>. Read once: if they reload
   * mid-flow the query is still there, and if it is absent this is an ordinary
   * new booking.
   */
  const rescheduleToken = new URLSearchParams(window.location.search).get("reschedule");
  /**
   * Dropped into somebody else's page.
   *
   * "I want it to be as plain as possible so that I can kind of control the
   *  content around it ... literally like it just starts to have you been
   *  with us before it starts here."
   *
   * So `?embed=1` takes off everything that belongs to a page of its own: the
   * full-height dark background, the card it sits in, and the logo and
   * business name at the top. What is left is the booking itself, on a
   * transparent background, so the host page's own heading, phone number and
   * reviews sit around it.
   */
  const embedded = new URLSearchParams(window.location.search).get("embed") === "1";

  const [menu, setMenu] = useState<Menu | null>(null);
  const [menuError, setMenuError] = useState<string | null>(null);
  const [menuLoading, setMenuLoading] = useState(true);

  const [step, setStep] = useState<Step>("service");
  /** Treatments in the visit, in the order they'll be performed. */
  const [picked, setPicked] = useState<Service[]>([]);
  const [provider, setProvider] = useState<ProviderPick>(null);
  // In person / video / phone — only when the lead treatment offers a choice.
  const [mode, setMode] = useState<Mode | null>(null);
  // Set while appending a treatment to an in-progress visit — holds the step
  // to return to if the client backs out of the menu.
  const [addReturn, setAddReturn] = useState<Step | null>(null);
  // First-time or returning. Null until they say (only asked when the
  // practice limits what new clients can book).
  const [visitor, setVisitor] = useState<Visitor | null>(null);
  /**
   * "a choice of what the new person wants … based on what they pick we can
   *  lead them to how to book consultation and requested service"
   *
   * Only asked of a first-timer. Somebody who has been in before knows what
   * they are booking and gets the whole list, as they did.
   */
  const [path, setPath] = useState<BookingPath | null>(null);

  /**
   * An ad knows what it was selling, so the click should not be asked again.
   *
   * "I want him to set up the ads as Google IV / Google weight loss /
   *  Google NAD … This way they are separated in the advertising and we follow
   *  them as when they come in from where and if they converted."
   *
   * So a link tagged `utm_campaign=weight-loss` lands on weight loss with the
   * question already answered. The tag is left in the address untouched - it
   * is what the Advertising page counts - and anything unrecognised just asks
   * the question as usual.
   */
  const campaignPath = useMemo(() => {
    if (typeof window === "undefined") return null;
    const q = new URLSearchParams(window.location.search);
    const raw = (q.get("for") || q.get("utm_campaign") || "").toLowerCase();
    if (!raw) return null;
    const words = raw.replace(/[_+-]+/g, " ");
    if (/weight/.test(words)) return "weight_loss";
    if (/testosterone|trt/.test(words)) return "testosterone";
    if (/peptide|sermorelin/.test(words)) return "peptides";
    if (/botox|filler|cosmetic|dysport/.test(words)) return "cosmetic";
    if (/\bnad\b|\biv\b|drip|injection|vitamin/.test(words)) return "iv";
    return null;
  }, []);

  /**
   * An iframe cannot size itself, so it says how tall it is and the host page
   * does it. ResizeObserver catches every step of the booking, every expanded
   * list and every device rotation, so nothing is ever cut off and there is
   * never a scrollbar inside a scrollbar.
   */
  const embedRootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!embedded || typeof window === "undefined") return;
    const send = () => {
      /**
       * Measure the CONTENT, never the document.
       *
       * Inside an iframe `documentElement` is stretched to the frame, so
       * measuring it reports back exactly the height the host just set - it
       * agrees with itself forever and the frame never grows. The booking's
       * own outermost element is the only thing that knows how tall the
       * booking actually is.
       */
      const el = embedRootRef.current;
      const h = Math.ceil(el ? el.getBoundingClientRect().height : document.body.scrollHeight);
      if (h > 0) window.parent?.postMessage({ type: "hh-booking:height", height: h }, "*");
    };
    send();
    const ro = new ResizeObserver(send);
    if (embedRootRef.current) ro.observe(embedRootRef.current);
    ro.observe(document.body);
    window.addEventListener("load", send);
    // A late web font or image can change the height after everything settles.
    const t = setTimeout(send, 1200);
    return () => { ro.disconnect(); window.removeEventListener("load", send); clearTimeout(t); };
  }, [embedded]);

  /**
   * Pass on the "their card landed" word from the tab we opened.
   *
   * The card page is opened BY this widget, so its opener is this frame, not
   * the page the frame sits in. It posts to both, and this forwards what it
   * gets - so the landing page hears about the booking however the browser
   * happens to have arranged the windows.
   */
  useEffect(() => {
    if (!embedded || typeof window === "undefined") return;
    const onMessage = (e: MessageEvent) => {
      const d = e.data as { type?: unknown } | null;
      if (!d || d.type !== "hh-booking:confirmed") return;
      try { window.parent?.postMessage({ type: "hh-booking:confirmed" }, "*"); } catch { /* ignore */ }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [embedded]);

  const [cursor, setCursor] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsByDate, setSlotsByDate] = useState<Record<string, boolean>>({});
  const [slotsRefresh, setSlotsRefresh] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [timeNotice, setTimeNotice] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  // The optional "also text me offers" box — never pre-ticked.
  const [smsOffers, setSmsOffers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookError, setBookError] = useState<string | null>(null);
  // The API couldn't find a past visit for a returning-only treatment; we
  // offer the one-tap way over to the first-visit options.
  const [returningBlocked, setReturningBlocked] = useState(false);
  // "IV vitamin consultations are only once every 6 months": the treatment
  // they picked needs one first, and this is the one to add.
  const [consultNeeded, setConsultNeeded] = useState<{ id: string; name: string; sameVisit: boolean } | null>(null);
  /** What they still have to come back and book, once the consult is done. */
  const [thenBook, setThenBook] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<{ starts_at: string; ends_at: string; service: string } | null>(null);
  /** "A credit card is required to secure and hold your appointment." Where
   *  they go to put one in, when they don't already have one on file. */
  const [cardUrl, setCardUrl] = useState<string | null>(null);
  /** The slot is taken but it is not a booking until a card is on it. */
  const [held, setHeld] = useState(false);
  const [holdMinutes, setHoldMinutes] = useState<number | null>(null);

  const localTz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  // Load the treatment menu. Re-fetched when they say whether they've been
  // here before: a first-timer isn't offered the phone line.
  useEffect(() => {
    setMenuLoading(true);
    fetch(`/api/public/booking/menu?returning=${visitor === "new" ? "0" : "1"}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Booking is unavailable right now");
        return r.json() as Promise<Menu>;
      })
      .then((d) => setMenu(d))
      .catch((e) => setMenuError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setMenuLoading(false));
  }, [visitor]);

  const lead = picked[0] ?? null;

  // Joined ids drive every availability call — one treatment or five.
  const serviceIdsParam = picked.map((s) => s.id).join(",");
  const totalMinutes = picked.reduce((m, s) => m + s.duration_minutes, 0);
  // A consultation's fee is not shown anywhere on this page, so it must not
  // reappear inside a total either.
  const totalPriceCents = picked.reduce((m, s) => m + (showsPrice(s) ? s.price_cents : 0), 0);
  const visitName = picked.map((s) => s.name).join(" + ");

  const limitNew = menu?.new_client_limit === true;
  // What this visitor may pick from: a first-timer sees only the treatments
  // open to new clients.
  const menuForVisitor = useMemo<Menu | null>(() => {
    if (!menu) return null;
    /* "We have to remove Tirzepatide and Semaglutide from the been here
        before menus all that should be there is Return Weight Loss
        consultation … They can't book for the meds if they are returning
        they book the Return Consultation"
       The mirror of the rule below: somebody who has been in before is not
       offered a first visit's consultation. */
    if (visitor === "returning") {
      return { ...menu, services: menu.services.filter((sv) => sv.returning_clients !== false) };
    }
    if (!limitNew || visitor !== "new") return menu;
    let open = menu.services.filter((sv) => sv.new_clients === true);
    if (path) {
      // "Is it possible that the only options that show are what they can
      // book. For example if they chose weight loss they see only a weight
      // loss consultation." A first-timer must start with the consultation,
      // so that is all this step offers — the treatment comes after.
      open = open.filter((sv) => sv.counts_as_consult === true
        && path.packets.includes(sv.form_packet ?? ""));
    }
    return { ...menu, services: open };
  }, [menu, limitNew, visitor, path]);

  const slotsQuery = (date: string) => {
    if (!serviceIdsParam) return null;
    const params = new URLSearchParams({ service_ids: serviceIdsParam, date });
    if (mode) params.set("mode", mode);
    if (visitor === "new") params.set("returning", "0");
    return `/api/public/booking/slots?${params.toString()}`;
  };

  // Pre-fetch availability for the visible month so empty days grey out.
  useEffect(() => {
    if (!serviceIdsParam || provider === null) return;
    const days = monthDays(cursor);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const future = days.filter((d) => d.isCurrentMonth && d.date >= today).map((d) => isoDate(d.date));
    let cancelled = false;
    Promise.all(future.map(async (date) => {
      try {
        const url = slotsQuery(date);
        if (!url) return [date, false] as const;
        const r = await fetch(url);
        if (!r.ok) return [date, false] as const;
        const d = await r.json() as { slots: Slot[] };
        return [date, (d.slots ?? []).length > 0] as const;
      } catch { return [date, false] as const; }
    })).then((pairs) => {
      if (cancelled) return;
      const map: Record<string, boolean> = {};
      for (const [d, ok] of pairs) map[d] = ok;
      setSlotsByDate(map);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceIdsParam, provider, mode, cursor, slotsRefresh]);

  // Fetch slots for the chosen day.
  useEffect(() => {
    if (!serviceIdsParam || !selectedDate) return;
    const url = slotsQuery(selectedDate);
    if (!url) return;
    setSlotsLoading(true);
    fetch(url)
      .then((r) => r.ok ? r.json() : { slots: [] })
      .then((d) => setSlots(d.slots ?? []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceIdsParam, provider, mode, selectedDate, slotsRefresh]);

  // ── Step transitions ─────────────────────────────────────────────────────
  /**
   * Single funnel for every change to the treatment list. Any change moves
   * the visit's length, so the day/time picked against the old list is
   * always thrown away — and the provider is kept only if they can still
   * perform the lead treatment.
   */
  const applyServices = (next: Service[], keepProvider = false) => {
    setPicked(next);
    setAddReturn(null);
    setSelectedDate(null); setSelectedSlot(null); setSlots([]); setSlotsByDate({});
    setTimeNotice(null); setBookError(null); setReturningBlocked(false);
    if (next.length === 0) {
      setProvider(null);
      setMode(null);
      setStep("service");
      return;
    }
    /**
     * A consult that can be in the lounge, by video or by phone: ask how,
     * before anything else, so the times shown are the right kind.
     *
     * Except where this path limits a first-timer. "it was not clear that
     * after I booked the virtual consultation that I needed to then schedule
     * the iv service" - so a new client on the IV path is only offered the
     * lounge, where the consultation and the drip are one visit. With one way
     * left there is nothing to ask and the step disappears.
     */
    /* `online_modes` limits EVERYBODY on this path, not only a first-timer -
       "I don't want them to think that they can see them virtually" about the
       IV consultation, which a returning client was still being offered. */
    const everyone = path?.online_modes ?? null;
    const firstTimer = visitor === "new" ? path?.new_client_modes ?? null : null;
    const allowed = everyone && firstTimer
      ? everyone.filter((m) => firstTimer.includes(m))
      : everyone ?? firstTimer;
    const modes = (next[0]!.modes ?? []).filter((m) => !allowed || allowed.includes(m as Mode));
    const modeStillFits = mode !== null && modes.includes(mode);
    if (!(keepProvider && modeStillFits)) {
      if (modes.length > 1) { setMode(null); setProvider(null); setStep("mode"); return; }
      /**
       * One way left, so nothing to ask - but it must still be SAID, or the
       * times come back for every seat the treatment has. Leaving it null is
       * how a first-timer limited to the lounge would still have been offered
       * the video seat.
       */
      setMode(allowed && modes.length === 1 ? (modes[0] as Mode) : null);
    }
    goToDate();
  };

  /**
   * After the treatment (and the way of meeting), straight to when.
   *
   * There used to be a step in between asking the client to choose a person,
   * listing everybody by name and credential. "When we book we have to remove
   * the staff names" — so the booking takes whoever is free, which is what
   * almost every client picked anyway, and the desk can still move a visit to
   * a particular person afterwards.
   */
  const goToDate = () => {
    setProvider("any");
    setStep("date");
  };

  const pickService = (svc: Service) => {
    if (addReturn !== null && picked.length > 0) applyServices([...picked, svc], true);
    else applyServices([svc]);
  };

  const removeService = (id: string) => applyServices(picked.filter((s) => s.id !== id), true);

  /** Put the consultation her rule asks for at the FRONT of the visit, then
   *  go back for a time — the visit is longer now, so the old one may not
   *  fit. */
  const addConsultFirst = () => {
    if (!consultNeeded || !menu) return;
    const svc = menu.services.find((x) => x.id === consultNeeded.id);
    const wanted = picked.filter((x) => x.id !== consultNeeded.id).map((x) => x.name).join(" and ");
    const sameVisit = consultNeeded.sameVisit;
    setConsultNeeded(null);
    if (!svc) return;
    if (sameVisit) {
      applyServices([svc, ...picked.filter((x) => x.id !== svc.id)]);
      return;
    }
    // Video and phone consultations stand alone: book just the consultation,
    // and remember what they came for so we can say it on the way out.
    setThenBook(wanted || null);
    applyServices([svc]);
  };

  const addAnother = () => {
    setAddReturn(step);
    setBookError(null); setReturningBlocked(false);
    setStep("service");
  };

  const cancelAdd = () => {
    const back = addReturn ?? "date";
    setAddReturn(null);
    setStep(back);
  };

  const pickMode = (m: Mode) => {
    setMode(m);
    setSelectedDate(null); setSelectedSlot(null); setSlots([]); setSlotsByDate({});
    goToDate();
  };
  const backToMode = () => {
    setMode(null); setProvider(null);
    setSelectedDate(null); setSelectedSlot(null); setSlots([]); setSlotsByDate({});
    setTimeNotice(null); setBookError(null); setReturningBlocked(false);
    setStep("mode");
  };

  const backToService = () => {
    applyServices([]);
  };
  const backToDate = () => {
    setSelectedDate(null); setSelectedSlot(null);
    setTimeNotice(null); setBookError(null); setReturningBlocked(false);
    setStep("date");
  };
  const backToTime = () => {
    setSelectedSlot(null); setBookError(null); setReturningBlocked(false);
    setStep("time");
  };

  /** Switch between first-time and returning; starts the treatment pick over. */
  const chooseVisitor = (v: Visitor | null) => {
    setVisitor(v);
    setPath(null);
    setAddReturn(null);
    applyServices([]);
  };

  const choosePath = (p: BookingPath | null) => {
    setPath(p);
    setAddReturn(null);
    applyServices([]);
  };

  /** A first-timer is asked what they are here for before anything else. */
  // The menu arrives after the page does, so the pre-pick waits for it. It
  // only ever fills in the answer a first-timer was about to give.
  useEffect(() => {
    if (!campaignPath || path !== null) return;
    const match = (menu?.paths ?? []).find((p) => p.key === campaignPath);
    if (match) setPath(match);
  }, [campaignPath, menu?.paths, path]);

  const needsPath = limitNew && visitor === "new" && path === null
    && (menu?.paths?.length ?? 0) > 0;

  const submit = async () => {
    if (picked.length === 0 || !selectedSlot || !name.trim() || (!email.trim() && !phone.trim())) return;
    setSubmitting(true);
    setBookError(null);
    setReturningBlocked(false);
    setConsultNeeded(null);
    try {
      // Whoever the engine put on the time they chose.
      const providerId = selectedSlot.provider_id ?? null;
      const r = await fetch("/api/public/booking/book", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service_ids: picked.map((s) => s.id),
          provider_id: providerId,
          starts_at: selectedSlot.start,
          name: name.trim(),
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          notes: notes.trim() || undefined,
          sms_offers: smsOffers && !!phone.trim(),
          mode: mode ?? undefined,
          // Moving an existing visit. The old time is released by the server
          // only once this booking succeeds, so abandoning here costs nothing.
          replaces_token: rescheduleToken ?? undefined,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 409) {
        // Someone grabbed the slot while they were typing — refresh and re-pick.
        setSelectedSlot(null);
        setSlotsRefresh((n) => n + 1);
        setTimeNotice("That time was just taken. Please pick another.");
        setStep("time");
        return;
      }
      if (r.status === 400 && data.code === "consult_required" && typeof data.consult_service_id === "string") {
        // Her rule, not a refusal: offer to add the consultation to the
        // same visit rather than sending them away.
        setConsultNeeded({
          id: data.consult_service_id,
          name: String(data.consult_service_name ?? "consultation"),
          // A video or phone consultation is its own appointment; the drip
          // is another day. Only an in-person one can share the visit.
          sameVisit: data.same_visit !== false,
        });
        setBookError(typeof data.error === "string" ? data.error : null);
        return;
      }
      if (r.status === 400 && data.code === "returning_only") {
        // No past visit matched a returning-only treatment: the practice's
        // rule, not a bug. Offer the first-visit options.
        setReturningBlocked(true);
        setBookError(typeof data.error === "string" ? data.error : "We couldn't find a past visit for you.");
        return;
      }
      if (!r.ok) throw new Error(data.error || "Booking failed. Please try again.");
      setConfirmation({ starts_at: data.starts_at, ends_at: data.ends_at, service: `${data.service ?? visitName}${modePhrase(mode)}` });
      setCardUrl(typeof data.card_url === "string" ? data.card_url : null);
      setHeld(data.held === true);
      setHoldMinutes(typeof data.hold_minutes === "number" ? data.hold_minutes : null);
      setStep("done");
      /**
       * Tell the page we are embedded in what just happened.
       *
       *   "I want to make sure attribution is set so we can see which
       *    campaigns / ad groups / keywords converted within Google Ads"
       *
       * The ad tag lives on their landing page and a cross-domain frame
       * cannot fire it, so the frame reports and the page decides. Two
       * different things on purpose: a time HELD is not a booking until the
       * card is on it, and counting those as conversions would flatter the
       * campaign. Nothing personal or clinical crosses - no name, no contact
       * details, no treatment.
       */
      if (embedded) {
        try {
          window.parent?.postMessage(
            { type: data.held === true ? "hh-booking:held" : "hh-booking:booked" }, "*");
        } catch { /* a frame that refuses messages still books perfectly well */ }
      }
    } catch (e) {
      setBookError(e instanceof Error ? e.message : "Booking failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  // ── Shell states ─────────────────────────────────────────────────────────
  if (menuLoading) {
    return (
      <div ref={embedRootRef} className={cn("bg-zinc-950 px-4 py-8 sm:px-6 sm:py-12", !embedded && "min-h-screen")}>
        <div className="mx-auto max-w-5xl"><Skeleton className="h-96 rounded-2xl" /></div>
      </div>
    );
  }

  if (menuError || !menu || !menuForVisitor) {
    return (
      <div ref={embedRootRef} className={cn("bg-zinc-950 flex items-center justify-center px-4 py-12", !embedded && "min-h-screen")}>
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold text-zinc-200">Booking unavailable</h1>
          <p className="mt-2 text-sm text-zinc-500">{menuError ?? "Please check back shortly."}</p>
        </div>
      </div>
    );
  }

  if (step === "done" && confirmation) {
    return <DoneStep business={menu.business} confirmation={confirmation} contact={email.trim() || phone.trim()} thenBook={thenBook} cardUrl={cardUrl} held={held} holdMinutes={holdMinutes} embedded={embedded} />;
  }


  return (
    <div ref={embedRootRef} className={embedded
      ? "bg-zinc-950 px-4 py-5"
      : "min-h-screen bg-zinc-950 px-4 py-8 sm:px-6 sm:py-12"}>
      <div className={embedded
        ? "overflow-hidden"
        : "mx-auto max-w-5xl rounded-2xl border border-zinc-800/70 bg-zinc-900/30 shadow-2xl shadow-black/30 overflow-hidden"}>
        <div className={embedded
          ? "grid grid-cols-1"
          : "grid grid-cols-1 lg:grid-cols-[20rem_1fr]"}>
          {/* ── Left: business + running summary ── */}
          <aside className={embedded
            ? "px-0 pb-0 pt-0"
            : "border-b border-zinc-800/70 bg-zinc-950 p-6 lg:border-b-0 lg:border-r lg:p-8"}>
            {/* The host page already says who this is and shows the logo. */}
            {!embedded && (menu.business.logo_url ? (
              <img
                src={menu.business.logo_url}
                alt={menu.business.name}
                className="h-11 w-11 rounded-xl object-cover ring-1 ring-zinc-800"
              />
            ) : (
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-fuchsia-500/15">
                <Sparkles className="h-5 w-5 text-fuchsia-300" />
              </div>
            ))}
            {!embedded && (
              <>
                <h1 className="mt-5 text-xl font-semibold text-zinc-100">{menu.business.name}</h1>
                <p className="mt-1 text-sm text-zinc-500">Book your visit in a few taps.</p>
              </>
            )}

            <div className={embedded ? "space-y-3" : "mt-6 space-y-3"}>
              {limitNew && visitor !== null && (
                <SummaryRow label="Visit" onChange={() => chooseVisitor(null)}>
                  <span className="font-medium text-zinc-200">
                    {visitor === "new" ? "First visit" : "I've been here before"}
                  </span>
                </SummaryRow>
              )}
              {picked.map((s, i) => (
                <SummaryRow
                  key={`${s.id}-${i}`}
                  label={picked.length > 1 ? `Treatment ${i + 1}` : "Treatment"}
                  onChange={picked.length === 1 ? backToService : undefined}
                  onRemove={picked.length > 1 ? () => removeService(s.id) : undefined}
                >
                  <span className="font-medium text-zinc-200">{s.name}</span>
                  <span className="block text-xs text-zinc-500">
                    {s.duration_minutes} min{showsPrice(s) ? ` · ${priceLabel(s.price_cents)}` : ""}
                  </span>
                </SummaryRow>
              ))}
              {picked.length > 1 && (
                <p className="px-1 text-xs text-zinc-400">
                  About <span className="font-medium text-zinc-300">{durationLabel(totalMinutes)}</span> in the chair
                  {/* Only when something on the ticket actually shows a price -
                      a consultation on its own would otherwise read "Free". */}
                  {picked.some(showsPrice) && (
                    <>
                      <span className="text-zinc-600"> · </span>
                      <span className="font-medium text-zinc-300">{priceLabel(totalPriceCents)}</span> total
                    </>
                  )}
                </p>
              )}
              {picked.length > 0 && picked.length < MAX_SERVICES && step !== "service" && (
                <button
                  type="button"
                  onClick={addAnother}
                  className="inline-flex items-center gap-1 rounded px-1 text-xs font-medium text-zinc-400 hover:text-fuchsia-200"
                >
                  <Plus className="h-3 w-3" /> Add another treatment
                </button>
              )}
              {lead && mode !== null && step !== "mode" && (
                <SummaryRow label="How" onChange={backToMode}>
                  <span className="font-medium text-zinc-200">{MODE_LABEL[mode]}</span>
                </SummaryRow>
              )}
              {selectedDate && (step === "time" || step === "details") && (
                <SummaryRow label="Date" onChange={backToDate}>
                  <span className="font-medium text-zinc-200">
                    {formatDate(selectedDate + "T00:00:00", { weekday: "long", month: "long", day: "numeric" })}
                  </span>
                </SummaryRow>
              )}
              {selectedSlot && step === "details" && (
                <SummaryRow label="Time" onChange={backToTime}>
                  <span className="font-medium text-zinc-200">
                    {formatTime(selectedSlot.start)} - {formatTime(selectedSlot.end)}
                  </span>
                </SummaryRow>
              )}
            </div>

            {/* Embedded, this is the first thing on the page until something
                is picked - a stray line of small print above the question.
                The time step says the same thing where it matters. */}
            {(!embedded || picked.length > 0) && (
              <p className={cn("flex items-center gap-1.5 text-xs text-zinc-600", embedded ? "mt-3" : "mt-6")}>
                <Globe className="h-3 w-3" />
                {menu.business.timezone !== localTz ? "Times shown in your local time" : menu.business.timezone}
              </p>
            )}
          </aside>

          {/* ── Right: current step ── */}
          <div className="p-6 lg:p-8">
            {step === "service" && limitNew && visitor === null && (
              <VisitorStep onPick={chooseVisitor} />
            )}
            {step === "service" && needsPath && (
              <PathStep
                paths={menu.paths ?? []}
                onPick={choosePath}
                onBack={() => chooseVisitor(null)}
              />
            )}
            {/* A concierge visit is priced by where you are, so it is an
                enquiry rather than a booking. */}
            {step === "service" && !needsPath && path?.enquiry && (
              <EnquiryStep path={path} onBack={() => choosePath(null)} />
            )}
            {step === "service" && !needsPath && !path?.enquiry && (!limitNew || visitor !== null) && (
              <ServiceStep
                menu={menuForVisitor}
                note={visitor === "new"
                  // On the consultation step her blurb about how it works is
                  // what matters — "the money price appears and that could be
                  // confusing so we need to write a blurb". Falls back to her
                  // general first-visit line.
                  ? (menu.consult_note?.trim() || menu.first_visit_note || "")
                  : (menu.returning_note ?? "")}
                picked={picked}
                adding={addReturn !== null && picked.length > 0}
                visitor={limitNew ? visitor : null}
                onPick={pickService}
                onCancelAdd={cancelAdd}
                onSwitchVisitor={() => chooseVisitor(visitor === "new" ? "returning" : "new")}
              />
            )}
            {step === "mode" && lead && (
              <ModeStep modes={lead.modes ?? []} treatment={lead.name} onPick={pickMode} onBack={backToService} />
            )}
            {step === "date" && lead && (
              <DateStep
                cursor={cursor}
                onCursor={setCursor}
                slotsByDate={slotsByDate}
                onPick={(date) => { setSelectedDate(date); setSelectedSlot(null); setStep("time"); }}
              />
            )}
            {step === "time" && selectedDate && (
              <TimeStep
                date={selectedDate}
                slots={slots}
                loading={slotsLoading}
                selected={selectedSlot}
                notice={timeNotice}
                localNote={menu.business.timezone !== localTz}
                onBack={backToDate}
                onPick={(s) => { setSelectedSlot(s); setTimeNotice(null); setStep("details"); }}
              />
            )}
            {step === "details" && picked.length > 0 && selectedSlot && (
              <DetailsStep
                visitName={visitName}
                slot={selectedSlot}
                name={name} setName={setName}
                email={email} setEmail={setEmail}
                phone={phone} setPhone={setPhone}
                notes={notes} setNotes={setNotes}
                sms={menu.sms}
                smsOffers={smsOffers} setSmsOffers={setSmsOffers}
                returning={limitNew && visitor === "returning"}
                consultNeeded={consultNeeded}
                onAddConsult={addConsultFirst}
                error={bookError}
                onFirstVisitOptions={returningBlocked ? () => chooseVisitor("new") : undefined}
                submitting={submitting}
                onSubmit={submit}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Aside summary row ─────────────────────────────────────────────────────
function SummaryRow({
  label, onChange, onRemove, children,
}: {
  label: string;
  onChange?: () => void;
  onRemove?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-zinc-800/70 bg-zinc-900/40 px-3 py-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 text-sm">
          <span className="block text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{label}</span>
          <span className="mt-0.5 block truncate">{children}</span>
        </div>
        {onChange && (
          <button
            type="button"
            onClick={onChange}
            className="shrink-0 rounded text-xs font-medium text-fuchsia-300/90 hover:text-fuchsia-200"
          >
            Change
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove this treatment"
            className="shrink-0 rounded text-xs font-medium text-zinc-500 hover:text-rose-300"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Step 1: pick a treatment ──────────────────────────────────────────────
function ServiceStep({
  menu, note, picked, adding, visitor, onPick, onCancelAdd, onSwitchVisitor,
}: {
  /** Already narrowed to what this visitor may book. */
  menu: Menu;
  /** Her guidance for a first visit, from Settings. */
  note: string;
  picked: Service[];
  /** True while appending a treatment to a visit already in progress. */
  adding: boolean;
  /** Null when the practice doesn't limit new clients (nothing to switch). */
  visitor: Visitor | null;
  onPick: (s: Service) => void;
  onCancelAdd: () => void;
  onSwitchVisitor: () => void;
}) {
  const groups = useMemo(() => bookingSections(menu.categories, menu.services), [menu]);

  /**
   * "if they hit IV drip, all they see is IV drips and not IM"
   *
   * Her IV family is sixteen drips, eleven injections, nine pushes and six
   * NAD infusions. Asking which kind first turns a forty-item scroll into one
   * tap, and nobody reads past the injections to find a drip. One section and
   * there is nothing to ask, so the question does not appear - which is the
   * case for a first visit, where the list is the single consultation.
   */
  const [section, setSection] = useState<string | null>(null);
  // Starting "add another treatment" asks again, rather than reopening at
  // whatever they happened to be looking at last time.
  useEffect(() => { setSection(null); }, [adding]);
  /**
   * "it falls higher on the page not in view (lap top view) … it's
   *  misleading and they could think nothing is there … it's because there
   *  is only one selection for peptide"
   *
   * Picking a section swaps the list in place. Coming from a long list of
   * sections the page is already scrolled down, so a SHORT section (Men's
   * Health has one treatment in it) draws above the fold and the client is
   * left looking at blank space. Put the top of it back in front of them.
   */
  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!section) return;
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [section]);
  const asksSection = groups.length > 1;
  const current = asksSection ? groups.find((g) => g.name === section) ?? null : groups[0] ?? null;
  const shown = current ? [current] : [];
  const pickedIds = new Set(picked.map((s) => s.id));

  // Backing out of a section goes to the sections, not out of the booking.
  const back = asksSection && current
    ? <BackLink onClick={() => setSection(null)}>All sections</BackLink>
    : adding ? <BackLink onClick={onCancelAdd}>Back to my booking</BackLink> : null;

  return (
    <div ref={topRef} style={{ scrollMarginTop: "1rem" }}>
      {back}
      <h2 className={cn("text-lg font-semibold text-zinc-100", (adding || back) && "mt-3")}>
        {adding && !current ? "Add another treatment"
          : visitor === "new" ? "Choose your consultation"
            : current ? current.name
              : "What are you booking?"}
      </h2>
      {/* Inside a section the heading is the section's own name, so the line
          about finding their file belongs on the way in, not here. */}
      <p className={cn("mt-0.5 text-xs text-zinc-500", asksSection && current && "hidden")}>
        {asksSection && !current
          ? adding
            ? "Pick where the next treatment is, and we’ll run it back-to-back with what you’ve already chosen."
            : "Pick the kind of treatment and we’ll show you just those."
          : adding
          ? "We’ll run it back-to-back with what you’ve already chosen, and your times will refresh."
          : visitor === "new"
            // Was "New clients start with an IV drip or a consultation",
            // which stopped being true once a first-timer picked what they
            // were coming in for and was shown only its consultation.
            ? "Your first visit starts here. You’ll choose your treatment straight after."
            : visitor === "returning"
              ? "Book any treatment. We’ll find your file with the mobile number or email you gave us before."
              : "Pick the service you’d like to book."}
      </p>
      {note && !adding && (
        <p className="mt-3 rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 px-3 py-2 text-xs text-zinc-300">{note}</p>
      )}
      {visitor !== null && !adding && (
        <button
          type="button"
          onClick={onSwitchVisitor}
          className="mt-2 rounded text-xs font-medium text-fuchsia-300/90 hover:text-fuchsia-200"
        >
          {visitor === "new" ? "I’ve been here before" : "I’m new here"}
        </button>
      )}

      {/* The sections, when there is more than one to choose from. */}
      {asksSection && !current && (
        <div className="mt-5 space-y-2">
          {groups.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => setSection(g.name)}
              className="group flex w-full items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/30 px-4 py-3.5 text-left hover:border-fuchsia-500/50 hover:bg-zinc-900"
            >
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: paletteHex(g.color) }}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-zinc-100 group-hover:text-white">{g.name}</span>
                <span className="block text-xs text-zinc-500">
                  {g.services.length} {g.services.length === 1 ? "treatment" : "treatments"}
                </span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-zinc-600 group-hover:text-fuchsia-300" />
            </button>
          ))}
          {groups.length === 0 && (
            <p className="py-12 text-center text-sm text-zinc-500">No treatments are open for online booking right now.</p>
          )}
        </div>
      )}

      <div className="mt-5 space-y-6">
        {shown.map((g) => (
          <div key={g.id}>
            {/* No category heading here: either the section's own name is the
                heading above, or there is a single group and the heading above
                already frames it. A first-timer was reading
                "CONSULTATIONS & TEACHINGS" in capitals directly above the one
                consultation she had carefully named. */}
            <div className="space-y-2">
              {g.services.map((s) => {
                const alreadyPicked = adding && pickedIds.has(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    disabled={alreadyPicked}
                    onClick={() => onPick(s)}
                    className={cn(
                      "group w-full rounded-xl border px-4 py-3 text-left",
                      alreadyPicked
                        ? "cursor-default border-zinc-800/60 bg-zinc-900/10 opacity-60"
                        : "border-zinc-800 bg-zinc-900/30 hover:border-fuchsia-500/50 hover:bg-zinc-900",
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-zinc-100 group-hover:text-white">
                          {s.name}
                          {alreadyPicked && (
                            <span className="ml-2 rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
                              Added
                            </span>
                          )}
                        </p>
                        {s.description && (
                          <p className="mt-0.5 text-xs text-zinc-500 line-clamp-2">{s.description}</p>
                        )}
                        {s.booking_note && (
                          <p data-service-note className="mt-1.5 rounded-md border border-fuchsia-500/20 bg-fuchsia-500/5 px-2 py-1 text-[11px] text-zinc-300">
                            {s.booking_note}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 text-right text-xs text-zinc-400">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3 text-zinc-600" />
                          {s.duration_minutes} min
                        </span>
                        {showsPrice(s) && (
                          <>
                            <span className="mx-1.5 text-zinc-700">·</span>
                            <span className="font-medium text-zinc-300">{priceLabel(s.price_cents)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {!asksSection && shown.length === 0 && (
          <p className="py-12 text-center text-sm text-zinc-500">No treatments are open for online booking right now.</p>
        )}
      </div>
    </div>
  );
}

// ─── Step 0: first visit or returning ──────────────────────────────────────
function VisitorStep({ onPick }: { onPick: (v: Visitor) => void }) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-zinc-100">Have you been to us before?</h2>
      <p className="mt-0.5 text-xs text-zinc-500">So we show you the right treatments.</p>
      <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => onPick("new")}
          className="group rounded-xl border border-zinc-800 bg-zinc-900/30 px-4 py-4 text-left hover:border-fuchsia-500/50 hover:bg-zinc-900"
        >
          <p className="text-sm font-semibold text-zinc-100 group-hover:text-white">I’m new here</p>
          {/* The first step is now "what are you coming in for?", so this no
              longer promises a drip list. */}
          <p className="mt-1 text-xs text-zinc-500">We’ll ask what you’re coming in for.</p>
        </button>
        <button
          type="button"
          onClick={() => onPick("returning")}
          className="group rounded-xl border border-zinc-800 bg-zinc-900/30 px-4 py-4 text-left hover:border-fuchsia-500/50 hover:bg-zinc-900"
        >
          <p className="text-sm font-semibold text-zinc-100 group-hover:text-white">I’ve been here before</p>
          <p className="mt-1 text-xs text-zinc-500">Book any of our treatments.</p>
        </button>
      </div>
    </div>
  );
}

/**
 * A concierge enquiry.
 *
 * "Add a concierge IV Drip. And if they click IV drip they are directed to the
 *  form (a new one) that gets name email phone and the town they are in must
 *  be clear to town sometimes they give their home address and not where the
 *  service is needed"
 *
 * So the town field asks for the town the VISIT is in, and says so twice: in
 * the label and under it. Her concierge prices run by distance (Concierge 1 to
 * 4, and Montauk), so the wrong town is a wrong quote.
 */
function EnquiryStep({ path, onBack }: { path: BookingPath; onBack: () => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [town, setTown] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const send = async () => {
    setErr(null);
    if (!name.trim()) { setErr("Please give us your name."); return; }
    if (!email.trim() && !phone.trim()) { setErr("Please give a phone number or an email so we can reach you."); return; }
    if (!town.trim()) { setErr("Please tell us which town the visit is in."); return; }
    setBusy(true);
    try {
      const r = await fetch(`/api/public/forms/${path.enquiry!.slug}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(), email: email.trim() || undefined, phone: phone.trim() || undefined,
          town_of_visit: town.trim(), notes: notes.trim() || undefined,
        }),
      });
      if (!r.ok) throw new Error("That did not send");
      setSent(true);
    } catch {
      setErr("Sorry, that did not send. Please try again, or call us.");
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div>
        <h2 className="text-lg font-semibold text-zinc-100">Thank you</h2>
        <p className="mt-2 text-sm text-zinc-300">
          We have your details and someone will call you back today to arrange your visit
          and confirm the price for {town.trim()}.
        </p>
      </div>
    );
  }

  return (
    <div>
      <button type="button" onClick={onBack} className="mb-3 text-xs text-zinc-500 hover:text-zinc-300">
        ← Back
      </button>
      <h2 className="text-lg font-semibold text-zinc-100">{path.label}</h2>
      <p className="mt-0.5 text-xs text-zinc-400">{path.enquiry!.note}</p>
      <div className="mt-4 space-y-3">
        <label className="block">
          <span className="text-xs font-medium text-zinc-400">Your name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name"
            className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100" />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-medium text-zinc-400">Mobile</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" autoComplete="tel"
              className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-zinc-400">Email</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email"
              className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100" />
          </label>
        </div>
        <label className="block">
          <span className="text-xs font-medium text-zinc-400">
            Which town is the visit in?
          </span>
          <input value={town} onChange={(e) => setTown(e.target.value)} placeholder="e.g. Cutchogue, Southold, Montauk"
            className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100" />
          <span className="mt-1 block text-[11px] text-zinc-500">
            The town we are coming to, which may not be your home address.
          </span>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-zinc-400">Anything else? (optional)</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100" />
        </label>
        {err && <p className="text-xs text-rose-300">{err}</p>}
        <button type="button" onClick={() => void send()} disabled={busy}
          className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
          {busy ? "Sending…" : "Ask us to call back"}
        </button>
      </div>
    </div>
  );
}

// ─── Step 1a: what are you here for? ───────────────────────────────────────
/**
 * "Maybe we should have a drop down box and a choice of what the new person
 *  wants … Then based on what they pick we can lead them to how to book
 *  consultation and requested service."
 *
 * Buttons rather than a dropdown: there are three of them, and a dropdown
 * hides the choices behind a tap on a phone.
 */
function PathStep({
  paths, onPick, onBack,
}: { paths: BookingPath[]; onPick: (p: BookingPath) => void; onBack: () => void }) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-zinc-100">What are you coming in for?</h2>
      <p className="mt-0.5 text-xs text-zinc-500">So we only show you what applies.</p>
      <div className="mt-5 grid grid-cols-1 gap-2">
        {paths.map((p) => (
          <button
            key={p.key}
            type="button"
            data-booking-path={p.key}
            onClick={() => onPick(p)}
            className="group rounded-xl border border-zinc-800 bg-zinc-900/30 px-4 py-4 text-left hover:border-fuchsia-500/50 hover:bg-zinc-900"
          >
            <p className="text-sm font-semibold text-zinc-100 group-hover:text-white">{p.label}</p>
            <p className="mt-1 text-xs text-zinc-500">{p.blurb}</p>
          </button>
        ))}
      </div>
      <button type="button" onClick={onBack} className="mt-4 text-xs text-zinc-500 underline hover:text-zinc-300">
        Back
      </button>
    </div>
  );
}

// ─── Step 1b: in person, video or phone ────────────────────────────────────
function ModeStep({
  modes, treatment, onPick, onBack,
}: {
  modes: Mode[];
  treatment: string;
  onPick: (m: Mode) => void;
  onBack: () => void;
}) {
  const OPTIONS: Record<Mode, { title: string; body: string; Icon: React.ComponentType<{ className?: string }> }> = {
    in_person: { title: "In person", body: "At the clinic.", Icon: Building2 },
    video: { title: "Video call", body: "From wherever you are.", Icon: Video },
    phone: { title: "Phone call", body: "We call you at your appointment time.", Icon: Phone },
  };
  return (
    <div>
      <BackLink onClick={onBack}>Treatments</BackLink>
      <h2 className="mt-3 text-lg font-semibold text-zinc-100">How would you like to meet?</h2>
      <p className="mt-0.5 text-xs text-zinc-500">{treatment} can happen any of these ways.</p>
      <div className="mt-5 space-y-2">
        {modes.map((m) => {
          const o = OPTIONS[m];
          return (
            <button
              key={m}
              type="button"
              onClick={() => onPick(m)}
              className="group w-full rounded-xl border border-zinc-800 bg-zinc-900/30 px-4 py-3.5 text-left hover:border-fuchsia-500/50 hover:bg-zinc-900"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-fuchsia-500/15">
                  <o.Icon className="h-4 w-4 text-fuchsia-300" />
                </div>
                <div>
                  <p className="text-sm font-medium text-zinc-100 group-hover:text-white">{o.title}</p>
                  <p className="text-xs text-zinc-500">{o.body}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step 3: pick a date ───────────────────────────────────────────────────
function DateStep({
  cursor, onCursor, slotsByDate, onPick,
}: {
  cursor: Date;
  onCursor: (d: Date) => void;
  slotsByDate: Record<string, boolean>;
  onPick: (date: string) => void;
}) {
  const days = useMemo(() => monthDays(cursor), [cursor]);
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const atCurrentMonth =
    cursor.getFullYear() === today.getFullYear() && cursor.getMonth() === today.getMonth() ||
    new Date(cursor.getFullYear(), cursor.getMonth(), 1) < today;

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-zinc-100">Pick a date</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={atCurrentMonth}
            onClick={() => onCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            aria-label="Previous month"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100 disabled:cursor-default disabled:text-zinc-700 disabled:hover:bg-transparent"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="min-w-32 text-center text-base font-medium text-zinc-200">
            {cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
          </span>
          <button
            type="button"
            onClick={() => onCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            aria-label="Next month"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="py-2 text-center text-xs font-semibold uppercase tracking-wider text-zinc-500">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 place-items-center">
        {days.map(({ date, isCurrentMonth }) => {
          const iso = isoDate(date);
          const isPast = date < today;
          const isToday = iso === isoDate(today);
          const hasSlots = slotsByDate[iso];
          // Unknown availability (still prefetching) stays enabled optimistically.
          const disabled = !isCurrentMonth || isPast || hasSlots === false;
          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              data-day={iso}
              onClick={() => onPick(iso)}
              className={cn(
                "relative h-9 w-9 sm:h-10 sm:w-10 rounded-full text-sm font-medium transition-all flex items-center justify-center",
                disabled && "text-zinc-700",
                !disabled && "text-fuchsia-200 bg-fuchsia-500/10 hover:bg-fuchsia-500 hover:text-white hover:shadow-md hover:shadow-fuchsia-500/20 cursor-pointer",
                isToday && !disabled && "ring-1 ring-fuchsia-400/60",
              )}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      <p className="mt-5 flex items-center gap-1.5 text-xs text-zinc-500">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-fuchsia-400" />
        Days with openings are highlighted
      </p>
    </div>
  );
}

// ─── Step 4: pick a time ───────────────────────────────────────────────────
function TimeStep({
  date, slots, loading, selected, notice, localNote, onBack, onPick,
}: {
  date: string;
  slots: Slot[];
  loading: boolean;
  selected: Slot | null;
  notice: string | null;
  localNote: boolean;
  onBack: () => void;
  onPick: (s: Slot) => void;
}) {
  return (
    <div>
      <BackLink onClick={onBack}>Pick another date</BackLink>
      <h2 className="mt-3 text-base font-semibold text-zinc-100">
        {formatDate(date + "T00:00:00", { weekday: "long", month: "long", day: "numeric" })}
      </h2>
      <p className="mt-0.5 text-xs text-zinc-500">
        Select a time{localNote && <span className="text-zinc-600"> · Times shown in your local time</span>}
      </p>

      {notice && (
        <p className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
          {notice}
        </p>
      )}

      <div className="mt-4">
        {loading ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
          </div>
        ) : slots.length === 0 ? (
          <p className="py-12 text-center text-sm text-zinc-500">No times open this day. Try another date.</p>
        ) : (
          <div className="grid max-h-96 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
            {slots.map((s) => (
              <button
                key={s.start + (s.provider_id ?? "")}
                type="button"
                onClick={() => onPick(s)}
                className={cn(
                  "rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors",
                  selected?.start === s.start
                    ? "border-fuchsia-500 bg-fuchsia-500/15 text-fuchsia-100"
                    : "border-zinc-800 bg-zinc-900/30 text-zinc-300 hover:border-fuchsia-500/50 hover:bg-zinc-900"
                )}
              >
                {formatTime(s.start)}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 5: details + confirm ─────────────────────────────────────────────
function DetailsStep({
  visitName, slot, name, setName, email, setEmail, phone, setPhone, notes, setNotes,
  sms, smsOffers, setSmsOffers, returning, error, onFirstVisitOptions, consultNeeded, onAddConsult, submitting, onSubmit,
}: {
  /** Every chosen treatment, joined — "Hydrafacial + Dermaplane". */
  visitName: string;
  slot: Slot;
  name: string; setName: (s: string) => void;
  email: string; setEmail: (s: string) => void;
  phone: string; setPhone: (s: string) => void;
  notes: string; setNotes: (s: string) => void;
  sms?: SmsConsent;
  smsOffers: boolean; setSmsOffers: (v: boolean) => void;
  /** They said they've been in before: we match them to their file. */
  returning: boolean;
  error: string | null;
  /** Set when her every-6-months consultation rule is what's in the way. */
  consultNeeded: { id: string; name: string; sameVisit: boolean } | null;
  onAddConsult: () => void;
  /** Set only when no past visit matched a returning-only treatment. */
  onFirstVisitOptions?: () => void;
  submitting: boolean;
  onSubmit: () => void;
}) {
  const contactMissing = !email.trim() && !phone.trim();
  return (
    <div>
      <h2 className="text-base font-semibold text-zinc-100">Almost done</h2>
      <p className="mt-0.5 text-xs text-zinc-500">
        {visitName} · {formatDate(slot.start, { weekday: "long", month: "short", day: "numeric" })} · {formatTime(slot.start)} - {formatTime(slot.end)}
      </p>

      <form className="mt-4 space-y-3" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
        <div>
          <Label required>Your name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="Jane Doe" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@example.com" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-1234" />
          </div>
        </div>
        <p className="text-[11px] text-zinc-500">
          {returning
            ? "Use the mobile number or email you gave us before, so we can find your file. At least one is needed for your confirmation."
            : "At least one of email or phone is required so we can send your confirmation."}
        </p>
        {/* Texting consent. Always on screen (carrier reviewers must see it
            without typing anything); the offers box is optional and unticked. */}
        {sms && (
          <div className="space-y-2 rounded-lg border border-zinc-800/70 bg-zinc-950/40 px-3 py-2.5" data-sms-consent>
            <p className="text-[11px] leading-relaxed text-zinc-400">
              {sms.appointment_notice}{" "}
              <a href={sms.terms_url} target="_blank" rel="noreferrer" className="underline hover:text-zinc-200">Texting terms</a>
              {" · "}
              <a href={sms.privacy_url} target="_blank" rel="noreferrer" className="underline hover:text-zinc-200">Privacy</a>
            </p>
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                checked={smsOffers}
                onChange={(e) => setSmsOffers(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-indigo-500"
              />
              <span className="text-[11px] leading-relaxed text-zinc-300">{sms.offers_consent}</span>
            </label>
          </div>
        )}
        <div>
          <Label>Anything we should know? (optional)</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. first visit, allergies, areas you'd like us to focus on"
          />
        </div>
        {/* Her every-6-months rule reads as an invitation, not a refusal. */}
        {consultNeeded && (
          <div data-consult-needed className="rounded-lg border border-indigo-500/40 bg-indigo-500/10 px-3 py-3">
            <p className="text-sm text-indigo-100">{error ?? `Your ${consultNeeded.name} is due first.`}</p>
            <Button type="button" size="sm" className="mt-2" onClick={onAddConsult}>
              {consultNeeded.sameVisit ? `Add ${consultNeeded.name}` : `Book ${consultNeeded.name} first`}
            </Button>
          </div>
        )}
        {error && !consultNeeded && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2">
            <p className="text-sm text-rose-300">{error}</p>
            {onFirstVisitOptions && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={onFirstVisitOptions}
              >
                See first-visit options
              </Button>
            )}
          </div>
        )}
        <Button type="submit" loading={submitting} disabled={!name.trim() || contactMissing} size="lg" className="w-full">
          Confirm booking
        </Button>
      </form>
    </div>
  );
}

// ─── Step 6: done ──────────────────────────────────────────────────────────
function DoneStep({
  business, confirmation, contact, thenBook, cardUrl, held, holdMinutes, embedded,
}: {
  business: Business;
  confirmation: { starts_at: string; ends_at: string; service: string };
  contact: string;
  /** What they came for, when the consultation had to be booked on its own. */
  thenBook?: string | null;
  /** Where to add the card that holds it — null when one is already on file. */
  cardUrl?: string | null;
  /** Taken, but not theirs until a card is on it. */
  held?: boolean;
  holdMinutes?: number | null;
  /** Inside somebody else's page: no full-page height, no empty dark below. */
  embedded?: boolean;
}) {
  return (
    <div className={cn("bg-zinc-950 flex items-center justify-center px-4 py-12 sm:px-6",
      !embedded && "min-h-screen")}>
      <div className="w-full max-w-md text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/20 ring-4 ring-emerald-500/10">
          <Check className={cn("h-7 w-7", held ? "text-amber-300" : "text-emerald-400")} />
        </div>
        {/* "to hold this appointment for you we require a credit card on file
            once the provide that then we say you are confirmed". So while it
            is only held it must not say booked - that is the whole point. */}
        <h1 className="mt-5 text-2xl font-semibold text-zinc-100">
          {held ? "We\u2019re holding this for you" : "You\u2019re booked!"}
        </h1>
        <p className="mt-3 text-base text-zinc-200">{confirmation.service}</p>
        <p className="mt-1 text-base text-zinc-300">
          {formatDate(confirmation.starts_at, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
        </p>
        <p className="mt-1 text-base text-zinc-400">
          {formatTime(confirmation.starts_at)} - {formatTime(confirmation.ends_at)}
        </p>
        {/* "the virtual for IV is complicated but so needed - how can we make
            it clear if booking virtual consultation online for vitamin
            therapy will have to schedule actual drip a different day". Said
            here, where they can act on it, rather than left to be discovered. */}
        {thenBook && (
          <div data-then-book className="mt-5 rounded-xl border border-fuchsia-500/30 bg-fuchsia-500/5 px-4 py-3 text-left">
            <p className="text-sm font-medium text-zinc-100">Your {thenBook} is a separate visit</p>
            <p className="mt-1 text-xs text-zinc-400">
              This consultation is on its own. Book your {thenBook} for any day after it and we&rsquo;ll be ready for you.
            </p>
            <a
              href="/booking"
              className="mt-2.5 inline-block rounded-lg bg-fuchsia-500/90 px-3 py-1.5 text-xs font-semibold text-white hover:bg-fuchsia-500"
            >
              Book my {thenBook}
            </a>
          </div>
        )}
        {/* The card is typed on the payment provider's own page; nothing here
            ever sees the number. Until it is done the time is HELD, not
            booked, and the wording says so plainly rather than hoping they
            read on. */}
        {cardUrl && (
          <div data-card-step className="mt-5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-left">
            <p className="text-sm font-medium text-zinc-100">Add your card to confirm it</p>
            <p className="mt-1 text-xs text-zinc-300">
              We&rsquo;re holding this time
              {typeof holdMinutes === "number" ? ` for ${holdMinutes} minutes` : ""} while you add a
              card. You won&rsquo;t be charged now - it&rsquo;s what holds the appointment - and we
              never see your card number.
            </p>
            {/**
              * Embedded in somebody else's page, the card page opens in its
              * own tab rather than inside the frame.
              *
              * A payment page in a third-party frame is at the mercy of the
              * browser's third-party rules - Safari in particular - and a
              * card step that quietly fails is a booking lost. Its own tab is
              * the practice's own address and has none of that. `rel=opener`
              * is deliberate: browsers cut the link back by default now, and
              * it is what lets the finished booking tell the landing page to
              * count its conversion.
              */}
            <a
              href={cardUrl}
              {...(embedded ? { target: "_blank", rel: "opener" } : {})}
              className="mt-2.5 inline-block rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-amber-400"
            >
              Add my card
            </a>
            <p className="mt-2 text-[11px] text-amber-200/80">
              Without a card this time goes back to somebody else.
            </p>
          </div>
        )}
        {contact && !held && (
          <p className="mt-4 text-xs text-zinc-500">A confirmation is on its way to {contact}.</p>
        )}
        {contact && held && (
          <p className="mt-4 text-xs text-zinc-500">
            Your confirmation goes to {contact} as soon as the card is on.
          </p>
        )}

        <div className="mt-8 rounded-xl border border-zinc-800/70 bg-zinc-900/40 p-4 text-left">
          <p className="flex items-center gap-2 text-sm font-medium text-zinc-200">
            <MessageSquareText className="h-4 w-4 text-fuchsia-300" />
            What happens next
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-zinc-400">
            We&rsquo;ll text you a confirmation, plus a couple of quick forms to fill out before your visit.
            See you soon at {business.name}!
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Small shared bits ─────────────────────────────────────────────────────
function BackLink({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md text-xs text-zinc-400 hover:text-zinc-200"
    >
      <ArrowLeft className="h-3 w-3" /> {children}
    </button>
  );
}

function priceLabel(cents: number): string {
  return cents === 0 ? "Free" : formatMoney(cents);
}

/**
 * What a consultation costs is deliberately NOT on the booking page.
 *
 * "The ppl booking iv we assume are thinking the consultation is the actual
 *  service and it kinda is so why put the dollar amount there. The reason for
 *  that dollar amount is to hold when they do the consultation and service on
 *  separate days since we eliminated this schedule for seperate days it's not
 *  necessary to have it show"
 *
 * The fee is unchanged and still rings up at the till, where it comes
 * straight back off the treatment. It is the SHOWING of it, on a step where
 * the consultation and the treatment read as one thing, that was confusing
 * people. Treatment prices are untouched.
 */
function showsPrice(s: { counts_as_consult?: boolean }): boolean {
  return s.counts_as_consult !== true;
}

/** "45 min" / "1 hr" / "1 hr 30 min" — the length of a stacked visit. */
function durationLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

// ─── Helpers ───────────────────────────────────────────────────────────────
// Local-date YYYY-MM-DD key (never toISOString — that keys against UTC and
// a 9pm PST slot would land on the wrong calendar day).
const isoDate = formatLocalDateKey;

/** 42-cell Sunday-start month grid, same shape as BookingPage. */
function monthDays(cursor: Date): { date: Date; isCurrentMonth: boolean }[] {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(start.getDate() - start.getDay());
  const out: { date: Date; isCurrentMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    out.push({ date: d, isCurrentMonth: d.getMonth() === cursor.getMonth() });
  }
  return out;
}
